/**
 * The declarations a client accepts once, at account level, before starting any form.
 *
 * WHY THIS EXISTS
 * Home Affairs requires promises to be made before a form can be lodged, and ADI relies on them as
 * the registered migration agent. Rather than repeat 9 to 21 tick-boxes on every form, the person
 * makes them ONCE for their account, and that acceptance covers every form they complete now or later.
 *
 * THERE ARE THREE SETS, NOT ONE. An applicant, a partner sponsor and a visitor sponsor are making
 * completely different promises, and it matters:
 *
 *   applicant        — "what I tell you is true". A truthfulness attestation.
 *   sponsor_partner  — 40SP. A FINANCIAL UNDERTAKING: responsibility for all financial obligations
 *                      to the Commonwealth, plus support and accommodation for the first 2 years.
 *   sponsor_visitor  — 1149. A SECURITY BOND that can be FORFEITED, plus financial obligations.
 *
 * Showing a sponsor the applicant's wording ("everything I tell ADI about my application is true")
 * would record an attestation about an application they do not have. Showing a partner sponsor the
 * visitor bond, or a visitor sponsor a 2-year accommodation undertaking, would record a promise they
 * were never asked to make. Both are false evidence, which is worse than recording nothing.
 *
 * WORDING STATUS
 * Plain-English wording drafted in-house (Tzvi's call, 2026-07-12). The SUBSTANCE is faithful to the
 * Immi declaration sets we hold as ground truth: 47SP p26-27 / 47SP_stage2 p22 / 1418-1419 p20 for
 * the applicant; `submission_declarations` on 40SP_sponsor (21 real items, the last 4 are signature
 * blocks) and 1149_family_visitor_sponsor (14 real items, the 15th is a signature block).
 *
 * SHIPPED AHEAD OF ELI'S SIGN-OFF, deliberately, and this is only safe because there are NO real
 * clients on production yet: nobody can attest to unapproved wording before real onboarding. When
 * Eli approves the final language, BUMP THE VERSION. An acceptance of one version is not an
 * acceptance of the next, so everyone is re-prompted automatically. Never edit an item in place
 * without bumping the version, or the stored proof stops matching what people actually agreed to.
 *
 * See tasks/SPEC-account-level-declarations.md.
 */

export type DeclarationAudience = 'applicant' | 'sponsor_partner' | 'sponsor_visitor';

export interface DeclarationItem {
  id: string;
  text: string;
}

export interface DeclarationSet {
  audience: DeclarationAudience;
  /** Encodes the audience, so the stored proof says WHICH set was accepted. */
  version: string;
  heading: string;
  intro: string;
  confirmLabel: string;
  items: readonly DeclarationItem[];
}

// ---------------------------------------------------------------------------
// Applicant — unchanged wording and unchanged VERSION. Bumping it would
// invalidate every existing acceptance and re-prompt people for no reason.
// ---------------------------------------------------------------------------

const APPLICANT: DeclarationSet = {
  audience: 'applicant',
  version: '2026-07-v1-pending-eli-signoff',
  heading: 'Before you start',
  intro:
    'The Department of Home Affairs needs you to confirm a few things before we can prepare and lodge a visa application for you. You only need to do this once. It covers every form in your portal.',
  confirmLabel: 'I have read the statements above and I confirm each of them is true.',
  items: [
    {
      id: 'truthful',
      text: 'Everything I tell ADI Immigration for my application is true, correct and complete, to the best of my knowledge.',
    },
    {
      id: 'nothing_withheld',
      text: 'I have not left out anything that could affect my application, even if I think it might count against me.',
    },
    {
      id: 'understand_false_info',
      text: 'I understand that giving false or misleading information, or a fraudulent document, can cause my visa to be refused or cancelled, and can affect future applications.',
    },
    {
      id: 'understand_after_grant',
      text: 'I understand this applies even after a visa is granted, and that a visa can be cancelled later if the information I gave was untrue.',
    },
    {
      id: 'report_changes',
      text: 'I will tell ADI Immigration as soon as anything changes that is relevant to my application, including my relationship, my address, my job, my health, or any criminal matter.',
    },
    {
      id: 'documents_genuine',
      text: 'Every document I provide is genuine, and any translation or copy is a true version of the original.',
    },
    {
      id: 'agent_may_rely',
      text: 'I understand ADI Immigration will rely on my answers when preparing and lodging my application with the Department of Home Affairs on my behalf.',
    },
    {
      id: 'privacy',
      text: 'I understand my personal information will be handled in line with the ADI Immigration Privacy Policy, and that it may be shared with the Department of Home Affairs and other bodies where the law allows or requires it.',
    },
    {
      id: 'own_answers',
      text: 'The answers I give are my own, and where someone helps me complete a form, the answers still reflect my own circumstances.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Partner sponsor (40SP). This is a FINANCIAL UNDERTAKING. Every item below maps
// to a numbered declaration on the 40SP; nothing here is invented.
// ---------------------------------------------------------------------------

const SPONSOR_PARTNER: DeclarationSet = {
  audience: 'sponsor_partner',
  version: '2026-07-sponsor-partner-v1-pending-eli-signoff',
  heading: 'What you are agreeing to as a sponsor',
  intro:
    'Sponsoring a partner is a formal commitment to the Australian Government, not just a supporting statement. Please read these carefully, because you are taking on real financial and legal obligations. You only need to do this once.',
  confirmLabel:
    'I have read the statements above, I understand the obligations I am taking on, and I agree to them.',
  items: [
    {
      id: 'financial_obligations',
      // 40SP #1
      text: 'I accept responsibility for all financial obligations to the Commonwealth that my partner incurs because of their stay in Australia. In plain terms, if they owe the Australian Government money as a result of being here, I can be asked to pay it.',
    },
    {
      id: 'support_two_years',
      // 40SP #2
      text: 'I agree to support my partner financially, and to help them with accommodation, for the first 2 years after their partner visa is granted, or after they first arrive in Australia on it.',
    },
    {
      id: 'accommodation_on_arrival',
      // 40SP #3
      text: 'I will make sure suitable accommodation is available for my partner when they arrive in Australia.',
    },
    {
      id: 'english_courses',
      // 40SP #4
      text: 'I will support my partner, as needed, to attend English language courses.',
    },
    {
      id: 'settle_and_interview',
      // 40SP #5, #7
      text: 'I will help my partner settle in Australia, and I will attend an interview with the Department if they ask me to.',
    },
    {
      id: 'tell_dept_if_support_ends',
      // 40SP #6, #9
      text: 'I will tell the Department in writing if the support I have promised comes to an end, if our relationship breaks down, if my partner dies, or if I withdraw my sponsorship.',
    },
    {
      id: 'information_true',
      // 40SP #8, #10, #11, #18
      text: "The information I give is complete, correct and up to date. I understand I may be prosecuted for giving false or misleading information or fake documents, and that my partner's visa may be refused, or cancelled later, if I do.",
    },
    {
      id: 'grant_may_proceed_anyway',
      // 40SP #14
      text: 'I understand my partner may still be granted a permanent partner visa even if our relationship breaks down and I withdraw my sponsorship, for example in cases involving family violence or a child of the relationship.',
    },
    {
      id: 'privacy_after_breakdown',
      // 40SP #15
      text: "I understand that if our relationship breaks down, Australian privacy law prevents the Department from telling me anything further about my partner's application.",
    },
    {
      id: 'sponsorship_limits',
      // 40SP #16, #17
      text: 'I understand I cannot sponsor another partner until 5 years have passed from the date of this application, and I cannot sponsor more than 2 partners in total, unless there are compelling circumstances.',
    },
    {
      id: 'authorise_checks',
      // 40SP #19, #20, #21
      text: 'I authorise the Department to obtain information about me from other government agencies and organisations, including Centrelink, law enforcement and financial institutions, to assess this sponsorship. Where I have disclosed convictions or charges, I acknowledge the Department may share that with the applicant and with anyone who can lawfully decide where a migrating child may live.',
    },
    {
      id: 'privacy_notice',
      // 40SP #12, #13
      text: "I have read the Department's privacy notice (form 1442i), and I understand the Department may collect, use and disclose my personal information, including biometric and other sensitive information, as it sets out.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Visitor sponsor (1149). A SECURITY BOND that can be FORFEITED. Different
// instrument from the 40SP, so a different set.
// ---------------------------------------------------------------------------

const SPONSOR_VISITOR: DeclarationSet = {
  audience: 'sponsor_visitor',
  version: '2026-07-sponsor-visitor-v1-pending-eli-signoff',
  heading: 'What you are agreeing to as a sponsor',
  intro:
    'Sponsoring a visitor is a formal commitment to the Australian Government. It can involve money you may not get back. Please read these carefully. You only need to do this once.',
  confirmLabel:
    'I have read the statements above, I understand the obligations I am taking on, and I agree to them.',
  items: [
    {
      id: 'security_bond',
      // 1149 #1, #3
      text: 'If the Department asks me to, I will lodge a security bond for each person I sponsor. I understand that bond can be forfeited, meaning I do not get the money back, if the person I sponsor breaks any of their visa conditions.',
    },
    {
      id: 'financial_obligations',
      // 1149 #5
      text: 'I accept responsibility for all financial obligations to the Commonwealth that the people I sponsor incur because of their stay in Australia. In plain terms, if they owe the Australian Government money as a result of being here, I can be asked to pay it.',
    },
    {
      id: 'compliance_responsibility',
      // 1149 #6
      text: 'I accept responsibility for the people I sponsor complying with the conditions they are allowed to enter Australia under.',
    },
    {
      id: 'visa_conditions',
      // 1149 #7, #8, #9, #10
      text: 'I understand the visitor must not work in Australia, must not stay past the end of their visa, cannot apply for a further stay while here, and must not study or train for more than 3 months.',
    },
    {
      id: 'breach_bars_me',
      // 1149 #11
      text: 'I understand that if any of those visa conditions are breached, I cannot be approved as a sponsor for 5 years.',
    },
    {
      id: 'may_be_refused_anyway',
      // 1149 #2
      text: 'I understand the visa application may still be refused, regardless of my sponsorship.',
    },
    {
      id: 'one_sponsorship_at_a_time',
      // 1149 #4
      text: 'I understand my sponsorship cannot be approved if I am already sponsoring someone else who holds a Visitor (Class FA) visa.',
    },
    {
      id: 'information_true',
      // 1149 #12
      text: 'The information I have given, in this form and in everything I attach to it, is true and correct.',
    },
    {
      id: 'privacy_notice',
      // 1149 #13, #14
      text: "I have read the Department's privacy notice (form 1442i), and I understand the Department may collect, use and disclose my personal information as it sets out.",
    },
  ],
};

export const DECLARATION_SETS: Record<DeclarationAudience, DeclarationSet> = {
  applicant: APPLICANT,
  sponsor_partner: SPONSOR_PARTNER,
  sponsor_visitor: SPONSOR_VISITOR,
};

export function getDeclarationSet(audience: DeclarationAudience): DeclarationSet {
  return DECLARATION_SETS[audience];
}

/** The exact text shown, in order. This is what gets stored as proof. */
export function declarationTexts(audience: DeclarationAudience): string[] {
  return getDeclarationSet(audience).items.map((d) => d.text);
}

/**
 * Digest of the rendered text, so the wording can be proven later without trusting today's source
 * code. The version is part of the payload, so two sets that happened to share wording still hash
 * differently. Uses SubtleCrypto, which requires a secure context (https or localhost).
 */
export async function declarationsSha256(
  audience: DeclarationAudience,
  texts: string[] = declarationTexts(audience),
): Promise<string> {
  const payload = `${getDeclarationSet(audience).version}\n${texts.join('\n')}`;
  const bytes = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
